import { ChevronRight, MagnifyingGlass, XMarkMini } from "@medusajs/icons"
import { clx } from "@medusajs/ui"
import { useQuery } from "@tanstack/react-query"
import { useDeferredValue, useMemo, useState, type ReactNode } from "react"
import { Link } from "react-router-dom"
import { money } from "../lib/format"
import { caps, t } from "../lib/i18n"
import { displayUrl } from "../lib/media"
import { hqApi } from "../lib/medusa"
import { NAV } from "../lib/nav"
import { Ref } from "./ui"

const fold = (value: string) =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/ς/g, "σ")
    .replace(/^[@#]/, "")

/**
 * One box that finds anything: a section ("booking", "larry"), a site order (W-46, a name, an
 * Instagram handle, a voucher), a custom order (1668, a handle, an item) or a product. The lists
 * load the first time something is typed, and are the same ones the pages use, so opening a result
 * is instant.
 */
export function GlobalSearch({ placeholder, className }: { placeholder?: string; className?: string }) {
  const [term, setTerm] = useState("")
  const deferred = useDeferredValue(term)
  const query = fold(deferred.trim())
  const active = query.length >= 2

  const orders = useQuery({ queryKey: ["hq", "orders"], queryFn: hqApi.orders, enabled: active, staleTime: 60_000 })
  const custom = useQuery({ queryKey: ["hq", "custom"], queryFn: hqApi.customOrders, enabled: active, staleTime: 120_000 })
  const products = useQuery({ queryKey: ["hq", "product-items"], queryFn: () => hqApi.productItems(), enabled: active, staleTime: 120_000 })

  const results = useMemo(() => {
    if (!active) return null
    const number = query.replace(/^[wc]-?/, "")
    // English and the chosen language both find a section, so «παραγγελίες» and "orders" lead to the same place.
    const sections = NAV.filter((item) =>
      fold(`${item.label} ${item.description} ${item.keywords ?? ""} ${t(item.label)} ${t(item.description)} ${item.keywords ? t(item.keywords) : ""}`).includes(query)
    ).slice(0, 4)
    const site = (orders.data?.orders ?? [])
      .filter((order) =>
        [order.ref, String(order.display_id ?? ""), order.customer, order.instagram ?? "", order.tracking ?? "", order.first_item ?? ""].some((field) => {
          const value = fold(field)
          return value.includes(query) || (number.length >= 1 && /^\d+$/.test(number) && value === number)
        })
      )
      .slice(0, 6)
    const customOrders = (custom.data?.orders ?? [])
      .filter((order) =>
        (/^\d+$/.test(number) && order.order_number.startsWith(number)) ||
        fold(order.handle).includes(query) ||
        order.items.some((item) => fold(item.title).includes(query))
      )
      .slice(0, 6)
    const productHits = (products.data?.products ?? []).filter((product) => fold(`${product.title} ${product.category ?? ""}`).includes(query)).slice(0, 6)
    return { sections, site, customOrders, productHits }
  }, [active, query, orders.data, custom.data, products.data])

  const loading = active && (orders.isLoading || custom.isLoading || products.isLoading)
  const empty = results && !loading && !results.sections.length && !results.site.length && !results.customOrders.length && !results.productHits.length

  return (
    <div className={clx("flex flex-col gap-y-2", className)}>
      <label className="bg-ui-bg-base shadow-elevation-card-rest focus-within:shadow-borders-interactive-with-active flex h-11 items-center gap-x-2 rounded-lg px-3">
        <MagnifyingGlass className="text-ui-fg-muted shrink-0" />
        <input
          type="search"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder={placeholder ?? t("Search orders, customers, products")}
          aria-label={t("Search HQ")}
          enterKeyHint="search"
          autoComplete="off"
          className="txt-compact-medium placeholder:text-ui-fg-muted min-w-0 flex-1 bg-transparent outline-none [&::-webkit-search-cancel-button]:hidden"
        />
        {term && (
          <button type="button" onClick={() => setTerm("")} aria-label={t("Clear the search")} className="text-ui-fg-muted hover:text-ui-fg-base -mr-1 rounded p-1">
            <XMarkMini />
          </button>
        )}
      </label>

      {results && (
        <div className="bg-ui-bg-base shadow-elevation-card-rest flex flex-col overflow-hidden rounded-lg" aria-live="polite">
          {loading && <p className="txt-small text-ui-fg-muted px-4 py-3">{t("Looking…")}</p>}
          {empty && <p className="txt-small text-ui-fg-muted px-4 py-3">{t("Nothing matches “{term}”. Try an order number, a name or part of a product's name.", { term: deferred.trim() })}</p>}
          {results.sections.length > 0 && (
            <Group title={t("Sections")}>
              {results.sections.map((item) => (
                <Hit key={item.to} to={item.to} lead={<item.icon className="text-ui-fg-muted" />} title={t(item.label)} detail={t(item.description)} />
              ))}
            </Group>
          )}
          {results.site.length > 0 && (
            <Group title={t("Site orders")}>
              {results.site.map((order) => (
                <Hit
                  key={order.id}
                  to={`/orders/${order.id}`}
                  lead={<Ref>{order.ref}</Ref>}
                  title={order.customer}
                  detail={[order.first_item, order.instagram ? `@${order.instagram}` : null].filter(Boolean).join(" · ")}
                  end={money(order.total)}
                />
              ))}
            </Group>
          )}
          {results.customOrders.length > 0 && (
            <Group title={t("Custom orders")}>
              {results.customOrders.map((order) => (
                <Hit
                  key={order.order_number}
                  to={`/custom/${encodeURIComponent(order.order_number)}`}
                  lead={<Ref>C-{order.order_number}</Ref>}
                  title={order.handle ? `@${order.handle.replace(/^@/, "")}` : t("No Instagram")}
                  detail={order.items.map((item) => item.title).join(", ")}
                  end={order.sale_total == null ? undefined : money(order.sale_total)}
                />
              ))}
            </Group>
          )}
          {results.productHits.length > 0 && (
            <Group title={t("Products")}>
              {results.productHits.map((product) => (
                <Hit
                  key={product.id}
                  to={`/products/${product.id}`}
                  lead={
                    <span className="bg-ui-bg-subtle block size-8 shrink-0 overflow-hidden rounded">
                      {product.thumbnail && <img src={displayUrl(product.thumbnail)} alt="" className="size-full object-cover" loading="lazy" />}
                    </span>
                  }
                  title={product.title}
                  detail={product.status === "published" ? (product.stock > 0 ? t.n(product.stock, "{n} in stock", "{n} in stock") : t("Sold out")) : t("Draft")}
                  end={product.price == null ? undefined : money(product.price)}
                />
              ))}
            </Group>
          )}
        </div>
      )}
    </div>
  )
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="border-ui-border-base border-b last:border-b-0">
      <p className="hc-eyebrow px-4 pb-1 pt-3">{caps(title)}</p>
      <ul className="pb-1">{children}</ul>
    </div>
  )
}

function Hit({ to, lead, title, detail, end }: { to: string; lead: ReactNode; title: ReactNode; detail?: string; end?: ReactNode }) {
  return (
    <li>
      <Link to={to} className="hover:bg-ui-bg-base-hover active:bg-ui-bg-base-pressed flex min-h-[48px] items-center gap-x-3 px-4 py-2">
        <span className="flex w-[64px] shrink-0 items-center">{lead}</span>
        <span className="min-w-0 flex-1">
          <span className="txt-compact-small-plus block truncate">{title}</span>
          {detail && <span className="txt-compact-xsmall text-ui-fg-muted block truncate">{detail}</span>}
        </span>
        {end && <span className="hc-data text-ui-fg-subtle shrink-0 text-[12px]">{end}</span>}
        <ChevronRight className="text-ui-fg-muted shrink-0" />
      </Link>
    </li>
  )
}
